/**
 * The event search box.
 *
 * The same contract as the lead search, and for the same reason: one box beats
 * a row of dropdowns, and whitespace means AND, so `norway qualified a` narrows
 * to Norwegian, qualified, top-tier arenas in the order somebody thinks.
 *
 * A token is interpreted as, in order:
 *   `mine` / `others` / `unassigned`  — ownership
 *   `covered` / `uncovered`           — whether anyone is assigned to go
 *   `past` / `dated` / `undated`      — where it sits in time
 *   `unverified`                      — the source is still a placeholder
 *   a stage key or label              — pipeline position
 *   `a`–`d`                           — computed tier
 *   anything else                     — substring over the event's text fields
 */
import { EVENT_STAGES, isUnverified } from '@/types/events'
import { haystack as joinFields, matchesTokens } from '@/shared/util/tokenSearch'
import type { ScoredEvent } from './repository/eventsRepository'
import { isPast } from './table'

export interface SearchContext {
  /** Display name of the signed-in user, for `mine` / `others`. */
  currentUser: string
}

const TIER_TOKEN = /^[abcd]$/

function haystack(e: ScoredEvent): string {
  return joinFields([
    e.id,
    e.name,
    e.category,
    e.country,
    e.region,
    e.venue,
    e.audience,
    e.website,
    e.ownerName,
    e.contactName,
    e.contactEmail,
    e.costTier,
    e.nextAction,
    e.sponsorshipModel,
    // The assigned people, so `bjarte` finds everything he is down to cover.
    e.assignments.map((a) => a.personName).join(' '),
  ])
}

function matchesToken(e: ScoredEvent, token: string, ctx: SearchContext): boolean {
  const owner = e.ownerName || e.ownerId
  if (token === 'mine') return owner === ctx.currentUser
  if (token === 'others') return Boolean(owner) && owner !== ctx.currentUser
  if (token === 'unassigned') return !owner

  if (token === 'covered') return e.assignments.length > 0
  if (token === 'uncovered') return e.assignments.length === 0

  if (token === 'past') return isPast(e)
  if (token === 'dated') return Boolean(e.startDate)
  if (token === 'undated') return !e.startDate

  if (token === 'unverified') return isUnverified(e.sourceVerified)

  const stage = EVENT_STAGES.find((s) => s.key === token || s.label.toLowerCase() === token)
  if (stage) return e.stage === stage.key

  // A single letter a–d is a tier. It is also a plausible substring, but a
  // one-letter substring search matches almost everything, so the tier reading
  // is the only useful one.
  if (TIER_TOKEN.test(token)) return e.computedTier?.toLowerCase() === token

  return haystack(e).includes(token)
}

export function matchesEventSearch(e: ScoredEvent, term: string, ctx: SearchContext): boolean {
  return matchesTokens(term, (token) => matchesToken(e, token, ctx), {
    // A multi-word stage label would otherwise never match, because neither
    // word is a stage on its own.
    isPhrase: (phrase) => EVENT_STAGES.some((s) => s.label.toLowerCase() === phrase),
  })
}
